import { FC, ReactNode } from "react";

import Stack from "@mui/material/Stack";

import { TableFiltersMobileDrawer } from "./TableFiltersMobileDrawer";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { TableToolbar } from "@/dashboard-design-system/DashboardTableCustomization/TableToolbar/TableToolbar";
import { Filtering } from "@/icons/outlined";

interface TableQuickFiltersProps {
  search: ReactNode;
  quickFilters: ReactNode;
  actions: ReactNode;
  isAdvanced: boolean;
  hasActiveFilters: boolean;
  isMobileOpen: boolean;
  mobileCustomFilters?: ReactNode;
  onOpen: () => void;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
}

export const TableQuickFilters: FC<TableQuickFiltersProps> = ({
  search,
  quickFilters,
  actions,
  isAdvanced,
  hasActiveFilters,
  isMobileOpen,
  mobileCustomFilters,
  onOpen,
  onClose,
  onApply,
  onReset,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();

  return (
    <>
      <TableToolbar
        flexWrap={isSmallScreen ? "nowrap" : "wrap"}
        data-test="payment-quick-filters"
        search={search}
        filters={
          isSmallScreen ? (
            <DashboardIconButton
              variant="outlined"
              vibe={hasActiveFilters ? "brand" : "neutral"}
              data-test="payment-mobile-filters"
              sx={(theme) => ({ width: theme.spacing(4.5), height: theme.spacing(4.5), flexShrink: 0 })}
              aria-label={t("dashboard", "paymentsPage.table.filters.open")}
              onClick={onOpen}
            >
              <Filtering />
            </DashboardIconButton>
          ) : !isAdvanced ? (
            quickFilters
          ) : null
        }
        actions={actions}
      />
      <TableFiltersMobileDrawer
        open={isMobileOpen || Boolean(mobileCustomFilters)}
        onClose={onClose}
        footer={
          !mobileCustomFilters && (
            <Stack gap={1} padding={2}>
              <DashboardButton
                variant="filled"
                vibe="brand"
                data-test="payment-quick-filter-apply"
                sx={(theme) => ({ height: theme.spacing(4.5) })}
                onClick={onApply}
              >
                {t("dashboard", "paymentsPage.table.filters.seeResults")}
              </DashboardButton>
              <DashboardButton
                variant="text"
                vibe="neutral"
                onClick={onReset}
                sx={(theme) => ({ height: theme.spacing(4.5) })}
              >
                {t("dashboard", "paymentsPage.table.filters.clearQuick")}
              </DashboardButton>
            </Stack>
          )
        }
      >
        {mobileCustomFilters ?? (
          <Stack paddingX={3} paddingTop={2} paddingBottom={2}>
            {quickFilters}
          </Stack>
        )}
      </TableFiltersMobileDrawer>
    </>
  );
};
